"use client";

import { useId, useRef } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { DocumentTreeControls } from "@/components/doc/left-sidebar/documents/DocumentTreeControls";
import { SearchList } from "@/components/search/SearchList";
import { Sidebar } from "@/components/sidebar";
import * as Skeleton from "@/components/skeleton/Skeleton";
import { useStore } from "@/store";

import { DocumentTree } from "../documents/DocumentTree";
import { DocumentTreeDnD } from "../documents/DocumentTreeDnD";
import { NavInstructions } from "../NavInstructions";
import { TeamName } from "../team-dropdown/TeamDropdown";

export const PersonalDocumentTree = observer(function PersonalDocumentTree() {
  const { documentTree, auth, search } = useStore();
  const t = useTranslations("documentsSidebar");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const instructionsId = useId();

  if (!auth.isLoggedIn) {
    return null;
  }

  if (!documentTree.root) {
    return (
      <Sidebar.Group className="p-1.25" shrink>
        <Skeleton.Root>
          <div className="flex items-center h-9.5 p-1.25">
            <TeamName />
          </div>
          <Sidebar.GroupSection className="mt-1.25 sm:mt-0 p-0">
            <Skeleton.Block className="h-[70px]" />
          </Sidebar.GroupSection>
          <Sidebar.GroupSection className="mt-1.25 p-0">
            <Skeleton.Block className="h-[40px]" />
          </Sidebar.GroupSection>
        </Skeleton.Root>
      </Sidebar.Group>
    );
  }

  return (
    <Sidebar.Group className="p-0 relative flex-col" shrink>
      <DocumentTreeControls />
      {search.isOpen && search.searchQuery ? (
        <SearchList nested />
      ) : (
        <DocumentTreeDnD containerRef={scrollContainerRef}>
          <Sidebar.Scrollable
            as="nav"
            aria-label={t("documentTreePanel")}
            className="w-auto m-1.25 mt-1.5 rounded-8"
            ref={scrollContainerRef}
            aria-describedby={instructionsId}
          >
            <NavInstructions id={instructionsId} />
            <DocumentTree />
          </Sidebar.Scrollable>
        </DocumentTreeDnD>
      )}
    </Sidebar.Group>
  );
});
